/**
 * S6, the projects list. Home.
 *
 * ONE QUESTION for the person who has been here before: is everything fine,
 * across everything I have built. The ledger answers it in one downward glance
 * and then gets out of the way.
 *
 * A DIFFERENT QUESTION for the person who has never been here: what is this,
 * what is a project, and what am I supposed to do. The screen used to answer
 * none of the three. It rendered an H1 reading "Projects", a button, and four
 * unlabelled columns, and the loudest machine fact on the row was a commit
 * SHA. That is a correct screen for Priya on day 40 and an unreadable one for
 * Nina on day 0, and Nina is the design target.
 *
 * WHAT WAS CONFUSING, and what each change does about it. This list is the
 * actual review of the previous file, kept here because the next person to
 * open it will want to know why the deviations below were worth taking.
 *
 *   1. Nothing said what a project is. Screen spec S6 forbids a page
 *      description, citing Vercel's 2026 lesson that projects are the first
 *      thing on screen. RECORDED DEVIATION: one line of description now sits
 *      under the H1. Vercel can drop the sentence because every one of their
 *      users arrived through a git import and already knows what a project is.
 *      A person who has never deployed anything does not, and one line is a
 *      cheaper fix than a support conversation.
 *
 *   2. The commit SHA was in Simple mode. The brief's own sorting test, rule
 *      four, says an identifier belongs in Advanced. It has moved: Simple now
 *      reads "nina, 2 hours ago" and Advanced appends the sha to the END of
 *      that line. It used to be inserted between the author and the time,
 *      which pushed the timestamp sideways every time somebody touched the
 *      switch, so the comment claiming nothing else moved was not true. It is
 *      now: the sha arrives after the time and nothing before it shifts.
 *      Additive, per the Advanced contract, and the deploy row agrees, which
 *      is what stops the same fact being Advanced here and Simple there.
 *
 *   3. Four columns, no headers. A person reading a commit message in column
 *      two had no way to know what column two was. There is a hairline column
 *      header row now, at the ledger's own scale, hidden when the rows stack.
 *
 *   4. The empty screen apologised: a mark, "You haven't built anything
 *      yet.", one button. A three line primer explaining what a project is
 *      replaced it, and was itself cut: an empty account is not owed a
 *      lecture, and copy that recites what the product does reads as filler
 *      to the person it is aimed at. What is left is the lede and the one
 *      action, which is what an empty account actually has to do.
 *
 *   5. There was no way to search and no way to ask for help from this screen.
 *      Both now live in the top bar, on every screen, and the first run tour
 *      points at them. Neither belongs to this file.
 *
 * ROWS, NOT CARDS, and this survives the rebuild unchanged. Screen spec S6
 * draws a three column card grid. Four cards put four status pills in four
 * different places on the screen, which turns "is everything fine" from one
 * glance into four. A ledger puts every pill in one column, every web address
 * in one column and every deploy time in one column, so the answer is read
 * down a line rather than hunted around a grid. It is also the Instrument
 * doctrine the kit committed to: hairlines, not cards.
 *
 * THE ONE INK BLOCK is "New project", and it sits in the header on both the
 * empty and the populated screen, because an empty account has exactly one
 * thing to do and offering it twice is offering it badly.
 *
 * THE GRADUATE ROW is the only row that says something other than a deploy:
 * Soi Cats has arrived from Loop and its claim is still open, so the row says
 * so and offers the one action that finishes it. This is the single place in
 * the whole product surface where the word Loop appears.
 *
 * Nothing on this screen moves once the data lands. The only motion is the
 * account strip's three tracks while the usage numbers resolve, and they are
 * the last thing on the page.
 */

import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router';

import { Button } from '../../components/primitives/Button';
import { StatusPill } from '../../components/patterns/StatusPill';
import { VisitLink } from '../../components/patterns/VisitLink';
import { PendingInvitations } from '../../components/patterns/PendingInvitations';
import { CreditsSpentBar } from '../../components/patterns/CreditsSpentBar';
import { SectionPanel } from '../../components/patterns/SectionPanel';
import { Skeleton } from '../../components/patterns/Skeleton';
import { UsageStrip } from '../../components/patterns/UsageStrip';

import { environmentOf, getUsage, listDeploys, listProjects } from '../../lib/api';
import { useProjects } from '../../lib/auth/use-app-data';
import { count, shortSha } from '../../lib/format';
import { useAdvanced } from '../../lib/hooks/use-advanced';
import { useAsync } from '../../lib/hooks/use-async';
import { t } from '../../lib/i18n/t';
import { useDemoStore } from '../../lib/mock/store';
import { captureEvent } from '../../lib/analytics/posthog';
import type { Deploy, Project } from '../../lib/types';

import '../../styles/components/projects.css';

/**
 * The latest deploy on a project, newest first. A project with no deploys yet
 * answers undefined rather than an error: every brand new project passes
 * through this state.
 */
function newest(deploys: Deploy[]): Deploy | undefined {
  return [...deploys].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  )[0];
}

function host(webAddress: string): string {
  return webAddress.replace(/^https?:\/\//, '');
}

function whenLabel(iso: string): string {
  const diff = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (diff < 60) return t('just now');
  if (diff < 3600) return t('{n}m ago', { n: String(Math.floor(diff / 60)) });
  if (diff < 86400) return t('{n}h ago', { n: String(Math.floor(diff / 3600)) });
  return t('{n}d ago', { n: String(Math.floor(diff / 86400)) });
}

function countLabel(n: number): string {
  if (n === 1) return t('1 project');
  return t('{n} projects', { n: count(n) });
}

/* ------------------------------------------------------------- the row */

function ProjectRow({ project, advanced, scenarioId }: { project: Project; advanced: boolean; scenarioId: string }) {
  const address = environmentOf(project).webAddress;
  const claiming = project.claimToken !== null;

  const { data: deploys, isLoading: deploysLoading } = useAsync(
    () => listDeploys(project.id),
    [project.id, scenarioId],
  );
  const latest = deploys ? newest(deploys) : undefined;

  return (
    <li className="prow">
      <div className="prow__id">
        <h2 className="prow__name">
          <Link
            className="prow__link"
            to={`/p/${encodeURIComponent(project.slug)}`}
            onClick={() => {
              captureEvent('project_row_clicked', {
                slug: project.slug,
                projectId: project.id,
                status: project.status,
              });
            }}
          >
            {project.name}
          </Link>
          {/* Teams: an app somebody else owns and shared with you. */}
          {project.role === 'collaborator' ? (
            <span className="prow__shared">{t('Shared with you')}</span>
          ) : null}
        </h2>
        <p className="prow__addr mono" title={address}>
          {host(address)}
        </p>
        {/* Advanced appends one mono line per row and changes nothing else:
            not the order, not the row height budget, not what the row says. */}
        {advanced ? (
          <p className="prow__ident mono">
            {project.id}
            <span className="prow__dot" aria-hidden="true">
              ·
            </span>
            {project.region}
          </p>
        ) : null}
      </div>

      <div className="prow__last">
        {claiming ? (
          <>
            <p className="prow__claim">
              {t('Waiting for you to finish moving it in from Loop.')}
            </p>
            <Link
              className="prow__claim-action"
              to={`/claim/${encodeURIComponent(project.claimToken ?? '')}`}
              onClick={() => {
                captureEvent('graduate_claim_clicked', {
                  slug: project.slug,
                  projectId: project.id,
                });
              }}
            >
              {t('Finish moving it in')}
            </Link>
          </>
        ) : deploysLoading && latest === undefined ? (
          <p className="prow__meta mono">-</p>
        ) : latest ? (
          <>
            <p className="prow__message" title={latest.commitMessage}>
              {latest.commitMessage}
            </p>
            {/* Simple reads "nina, 2 hours ago". The sha is an identifier, and
                the brief's sorting test puts identifiers in Advanced, so it is
                appended to the end of this line when Advanced is on. The end,
                not the middle: inserted between the author and the time it
                moved the timestamp every time the switch was touched, and
                Advanced is not allowed to move anything. */}
            <p className="prow__meta mono">
              <span>{latest.author}</span>
              <span className="prow__dot" aria-hidden="true">
                ·
              </span>
              <span>{whenLabel(latest.createdAt)}</span>
              {advanced ? (
                <>
                  <span className="prow__dot" aria-hidden="true">
                    ·
                  </span>
                  <span>{shortSha(latest.commitSha)}</span>
                </>
              ) : null}
            </p>
          </>
        ) : (
          <p className="prow__claim">{t('Nothing deployed yet.')}</p>
        )}
      </div>

      <div className="prow__state">
        <StatusPill value={project.status} size="sm" />
      </div>

      <div className="prow__go">
        <VisitLink href={address} tone="quiet" />
      </div>
    </li>
  );
}

/* --------------------------------------------------------------- the page */

export default function ProjectsList() {
  const navigate = useNavigate();

  const scenarioId = useDemoStore((s) => s.scenarioId);
  const [advanced] = useAdvanced();

  const cachedProjects = useProjects();
  const { data: fetchedProjects, isLoading: isProjectsLoading } = useAsync(listProjects, [scenarioId]);
  // Production rows only. The server already scopes `listProjects`, but the
  // CACHED array can hold a staging environment the shell resolved for a page
  // visit (lib/hooks/use-resolve-project.ts), and an app appearing twice here
  // reads as two apps.
  const projects = (fetchedProjects ?? cachedProjects).filter(
    (p) => p.environment !== 'staging',
  );
  const isLoading = isProjectsLoading && projects.length === 0;

  const { data: usage } = useAsync(() => getUsage(null), [scenarioId]);

  useEffect(() => {
    if (!isLoading) {
      captureEvent('projects_list_viewed', {
        project_count: projects.length,
        has_projects: projects.length > 0,
      });
    }
  }, [isLoading, projects.length]);

  // Fires once the account strip has its numbers, so each breakdown's shape
  // (how many projects are named, whether an "Others" group exists) can be
  // read against how people use the list.
  useEffect(() => {
    const metrics = usage?.metrics.filter((m) => m.breakdown);
    if (!metrics?.length) return;
    captureEvent('account_usage_breakdown_viewed', {
      breakdowns: metrics.map((m) => ({
        resource: m.resource,
        named_projects: m.breakdown?.projects.length ?? 0,
        others_project_count: m.breakdown?.others?.projectCount ?? 0,
      })),
    });
  }, [usage]);

  if (isLoading) {
    return (
      <div className="page">
        <Head lede={t('Everything you have put on the internet lives here.')} />
        <div className="pl__ledger">
          <Skeleton shape="row" count={4} label={t('Loading your projects')} />
        </div>
      </div>
    );
  }

  if (projects.length === 0) {
    return (
      <div className="page">
        <Head
          stacked
          lede={t("Nothing here yet. Let's get started.")}
          action={
            <Button
              variant="solid"
              data-tour="new-project"
              onClick={() => {
                captureEvent('primer_new_project_clicked');
                navigate('/new');
              }}
            >
              {t('New project')}
            </Button>
          }
        />
        <PendingInvitations />
      </div>
    );
  }

  return (
    <div className="page">
      <Head
        lede={t('One line for each app you have put on the internet, and how it is doing.')}
        action={
          <Button variant="solid" data-tour="new-project" onClick={() => navigate('/new')}>
            {t('New project')}
          </Button>
        }
      />

      <PendingInvitations />

      <div className="pl__table">
        <div className="pl__cols" role="presentation" aria-hidden="true">
          <span>{t('Project')}</span>
          <span>{t('Last deploy')}</span>
          <span>{t('Status')}</span>
          <span />
        </div>

        <ul className="pl__ledger" aria-label={countLabel(projects.length)}>
          {projects.map((project) => (
            <ProjectRow
              project={project}
              advanced={advanced}
              scenarioId={scenarioId}
              key={project.id}
            />
          ))}
        </ul>

        <p className="pl__count">{countLabel(projects.length)}</p>
      </div>

      <SectionPanel
        title={t('Across your projects')}
        note={t('Your plan, added up over every project above.')}
      >
        <UsageStrip usage={usage ?? undefined} compact waitingLabel={t('Working out your usage')} />
        {usage?.creditsSpent ? <CreditsSpentBar spent={usage.creditsSpent} compact /> : null}
      </SectionPanel>
    </div>
  );
}

/* `stacked` is the empty account: the action sits under the sentence that
   invites it rather than across the page from it, because with no ledger
   underneath there is nothing on the right for it to line up with. The
   populated screen keeps the action on the title's own line, where it stays
   out of the way of the rows. */
function Head({
  lede,
  action,
  stacked = false,
}: {
  lede: string;
  action?: ReactNode;
  stacked?: boolean;
}) {
  return (
    <header className="pl__head" data-stacked={stacked ? 'yes' : 'no'}>
      <div className="pl__headline">
        <h1 className="page__title">{t('Projects')}</h1>
        <p className="pl__lede">{lede}</p>
      </div>
      {action ? <div className="pl__act">{action}</div> : null}
    </header>
  );
}
